/**
 * Overview — at-a-glance dashboard. Six cards arranged 2×3 covering the
 * questions you ask most often during a manual playtest:
 *
 *   1. Network / RPC          — am I pointing at the right cluster?
 *   2. Connected wallet       — what's my SOL balance?
 *   3. Treasury PDA           — does the program's pot have lamports?
 *   4. Active round           — pot, touches, king, time left
 *   5. Dev wallets summary    — how many test wallets do I have funded?
 *   6. Lobby / chat presence  — who's connected and chatting?
 *
 * Plus a "recent errors" strip at the bottom so failures from any tab
 * surface here without forcing the user to switch sections.
 */
import { BN } from "@coral-xyz/anchor";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { buildReadonlyProgram } from "../../explorer/anchor";
import { useChainNow } from "../../util/useChainNow";
import { KOTH_ROUND_LS_KEY } from "../../games/last-touch/useKothLobbyController";
import { useKothRound } from "../../games/last-touch/useKothRound";
import { useStore } from "../../store";
import { deriveTreasuryPda } from "../session";
import { useDevErrorLog } from "../store/devErrorLog";
import { useDevWallets } from "../store/devWallets";
import { btnStyle, colors, copyToClipboard, fmtRelTime, fmtSol, shortKey, styles } from "./_shared";

export function OverviewSection(): JSX.Element {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [searchParams] = useSearchParams();

  // Treasury + wallet balances (5s poll, shared with status bar but cheap).
  const programId = useMemo(() => buildReadonlyProgram(connection).programId, [connection]);
  const treasuryPda = useMemo(() => deriveTreasuryPda(programId), [programId]);
  const [treasuryLamports, setTreasuryLamports] = useState<number | null>(null);
  const [walletLamports, setWalletLamports] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const info = await connection.getAccountInfo(treasuryPda);
        if (!cancelled) setTreasuryLamports(info?.lamports ?? 0);
      } catch {
        if (!cancelled) setTreasuryLamports(null);
      }
      if (wallet.publicKey) {
        try {
          const lp = await connection.getBalance(wallet.publicKey);
          if (!cancelled) setWalletLamports(lp);
        } catch {
          if (!cancelled) setWalletLamports(null);
        }
      } else if (!cancelled) {
        setWalletLamports(null);
      }
      if (!cancelled) timer = setTimeout(tick, 5_000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [connection, treasuryPda, wallet.publicKey]);

  // Round tracked via URL ?round=… (shared source of truth with ChainTab).
  const roundIdParam = searchParams.get("round");
  const roundId = useMemo<BN | null>(() => {
    const raw =
      roundIdParam ??
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(KOTH_ROUND_LS_KEY)
        : null);
    if (!raw) return null;
    try {
      return new BN(raw, 10);
    } catch {
      return null;
    }
  }, [roundIdParam]);
  const { round } = useKothRound(roundId);
  const chainNow = useChainNow(1000);
  const secondsLeft = round
    ? Math.max(0, Number(round.expiresAt.toString()) - Math.floor(chainNow / 1000))
    : 0;

  // Live state via store.
  const localPlayer = useStore((s) => s.localPlayer);
  const remoteCount = useStore((s) => s.remotePlayers.size);
  const chatLog = useStore((s) => s.chatLog);
  const devWallets = useDevWallets((s) => s.wallets);
  const recentErrors = useDevErrorLog((s) => s.entries.slice(-5).reverse());

  const cluster =
    (import.meta.env.VITE_SOLANA_CLUSTER as string | undefined) ?? "(unset)";
  const endpoint = connection.rpcEndpoint;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionTitle}>Overview</div>
        <div style={styles.sectionSubtitle}>polls every 5s</div>
      </div>
      <div style={{ padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Card title="Network">
          <KV k="cluster" v={cluster} />
          <KV k="endpoint" v={endpoint} copy={endpoint} />
          <KV k="program" v={shortKey(programId.toBase58())} copy={programId.toBase58()} />
        </Card>

        <Card title="Connected wallet">
          <KV
            k="pubkey"
            v={shortKey(wallet.publicKey?.toBase58() ?? null)}
            copy={wallet.publicKey?.toBase58() ?? undefined}
          />
          <KV k="balance" v={`${fmtSol(walletLamports)} SOL`} />
          <KV k="adapter" v={wallet.wallet?.adapter.name ?? "(none)"} />
        </Card>

        <Card title="Treasury (singleton)">
          <KV k="pda" v={shortKey(treasuryPda.toBase58())} copy={treasuryPda.toBase58()} />
          <KV k="balance" v={`${fmtSol(treasuryLamports)} SOL`} />
          <KV k="status" v={treasuryLamports === null ? "not initialised" : "ok"} />
        </Card>

        <Card title="Active round">
          {round ? (
            <>
              <KV k="id" v={`#${round.roundId.toString()}`} />
              <KV k="pot" v={`${fmtSol(round.potLamports)} SOL`} />
              <KV k="touches" v={round.touchCount.toString()} />
              <KV k="king" v={shortKey(round.currentKing.toBase58())} copy={round.currentKing.toBase58()} />
              <KV
                k="time left"
                v={
                  round.status === 1
                    ? "settled"
                    : secondsLeft > 0
                      ? `${secondsLeft}s`
                      : "expired (run end_round)"
                }
              />
            </>
          ) : (
            <div style={{ ...styles.mono, color: colors.textMute }}>
              no round set — use Chain (ix) → init_round or set ?round= in URL
            </div>
          )}
        </Card>

        <Card title="Dev wallets">
          <KV k="count" v={devWallets.length.toString()} />
          <KV
            k="total"
            v={`${fmtSol(devWallets.reduce((acc, w) => acc + (w.balanceLamports ?? 0), 0))} SOL`}
          />
          <KV k="active" v={devWallets.find((w) => w.balanceLamports !== null)?.label ?? "—"} />
        </Card>

        <Card title="Lobby presence">
          <KV
            k="local"
            v={localPlayer ? `${localPlayer.displayName || "(unnamed)"} · ${localPlayer.id.slice(0, 8)}` : "(not joined)"}
          />
          <KV k="remotes" v={remoteCount.toString()} />
          <KV k="chat msgs" v={chatLog.length.toString()} />
        </Card>
      </div>

      <div style={{ padding: "0 12px 12px" }}>
        <Card title={`Recent errors${recentErrors.length ? ` (${recentErrors.length} of ${useDevErrorLog.getState().entries.length})` : ""}`}>
          {recentErrors.length === 0 ? (
            <div style={{ ...styles.mono, color: colors.textMute }}>(no errors)</div>
          ) : (
            recentErrors.map((e) => (
              <div key={e.id} style={errorRowStyle}>
                <span style={{ color: colors.textFaint, fontSize: 10 }}>
                  {fmtRelTime(e.ts)}
                </span>
                <span style={{ color: colors.fail, fontSize: 10, fontWeight: 700 }}>
                  {e.source}
                </span>
                <span style={{ ...styles.mono, flex: 1, color: colors.text }}>
                  {e.message.length > 140 ? `${e.message.slice(0, 140)}…` : e.message}
                </span>
                <button
                  onClick={() => void copyToClipboard(e.copyText)}
                  style={btnStyle({ tone: "ghost", small: true })}
                  title="Copy full error + stack"
                >
                  Copy
                </button>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={styles.card}>
      <div style={{ ...styles.label, color: colors.textMute }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </div>
  );
}

function KV({ k, v, copy }: { k: string; v: string; copy?: string }): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
      <div style={{ ...styles.label, width: 70, flex: "0 0 auto" }}>{k}</div>
      <div style={{ ...styles.mono, flex: 1 }}>{v}</div>
      {copy && (
        <button
          onClick={() => void copyToClipboard(copy)}
          style={btnStyle({ tone: "ghost", small: true })}
        >
          Copy
        </button>
      )}
    </div>
  );
}

const errorRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "4px 6px",
  borderTop: `1px solid ${colors.border}`,
};
